/**
 * BreadcrumbList JSON-LD emitter.
 *
 * Renders the Schema.org BreadcrumbList structured-data block — Google
 * uses it for "site › section › page" rich-snippets in SERPs, which
 * measurably increase CTR on long-tail terms. Server-component, no
 * client state.
 *
 * Usage:
 *   <BreadcrumbJsonLd
 *     items={[
 *       { name: 'Home', url: '/' },
 *       { name: 'Branchen', url: '/#industries' },
 *       { name: 'Friseur', url: '/friseur-online-buchung' },
 *     ]}
 *   />
 */
type BreadcrumbItem = {
  name: string;
  /** Absolute URL or path. Paths are resolved against NEXT_PUBLIC_APP_URL. */
  url: string;
};

interface BreadcrumbJsonLdProps {
  items: BreadcrumbItem[];
}

export default function BreadcrumbJsonLd({ items }: BreadcrumbJsonLdProps) {
  if (items.length === 0) return null;
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'https://eazybooking.de';
  const resolve = (url: string) =>
    url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: resolve(item.url),
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}
